import {
  ConfirmOtpResponse,
  ErrorResponse,
  ForgetPasswordResponse,
  LoginResponse,
  OtpResponse,
  ResumeResponse,
  ServiceResponse,
  StatusResponse,
  ValidateOtpResponse,
  Version,
} from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';
import { trackCommonEvents } from '../utils';
import { useAnalyticsStore } from './analytics.store';
import { useUserStore } from './user.store';

export type AuthState = {
  isInitialized: boolean;
  versionData: Version | null;
  rawVersionData: unknown | null;
  data: LoginResponse | ResumeResponse | null;
  isLoading: boolean;
  isLoadingToken: boolean;
  error: ErrorResponse | null;
  token: string | null;
  isLoggedIn: boolean;
};

export type AuthActions = {
  setIsLoading: (isLoading: boolean) => void;
  setIsLoadingToken: (isLoadingToken: boolean) => void;
  setError: (error: ErrorResponse | null) => void;
  checkLoggedIn: () => Promise<void>;
  loginWithUsernamePassword: (username: string, password: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithGoogle: (token: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithFacebook: (token: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithApple: (token: string, refreshToken: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithUUID: () => Promise<ServiceResponse<LoginResponse>>;
  logout: () => Promise<ServiceResponse<unknown>>;
  forgetPassword: (
    contact: string,
    type: 'email' | 'contact_number'
  ) => Promise<ServiceResponse<ForgetPasswordResponse>>;
  resetPassword: (
    contact: string,
    otp: string,
    refCode: string,
    password: string
  ) => Promise<ServiceResponse<StatusResponse>>;
  sendOtp: (contact: string, channel: string) => Promise<ServiceResponse<OtpResponse>>;
  validateOtp: (
    otp: string,
    refCode: string,
    contact: string,
    channel: string
  ) => Promise<ServiceResponse<ValidateOtpResponse>>;
  confirmOtp: (otp: string, refCode: string, contact: string) => Promise<ServiceResponse<ConfirmOtpResponse>>;
  resume: (clientVersion: string) => Promise<ServiceResponse<ResumeResponse>>;
  version: (clientVersion: string) => Promise<ServiceResponse<Version>>;
  clear: () => void;
};

export type AuthStore = AuthState & AuthActions;

type LoginHandlerParams = {
  loginFn: () => Promise<ServiceResponse<LoginResponse>>;
};

export const useAuthStore = create<AuthStore>((set) => {
  // Private helper function to handle common login logic
  const handleLogin = async ({ loginFn }: LoginHandlerParams): Promise<ServiceResponse<LoginResponse>> => {
    const { bzbsService, tokenFunctions, tokenType } = useBuzzebeesAppStore.getState();

    if (!bzbsService || !tokenFunctions) {
      return { type: 'client-error', message: 'Service or auth persistence not configured' };
    }

    set({ isLoading: true, error: null });
    const response = await loginFn();
    set({ isLoading: false });

    if (response.type === 'success') {
      const token = tokenType === 'jwt' ? response.model.jwt : response.model.token;
      tokenFunctions.setToken(token);

      set({
        data: response.model,
        token: response.model.token,
        isLoggedIn: true,
        error: null,
      });
      useAnalyticsStore.getState().setUserId(response.model.userId);
      useAnalyticsStore.getState().setCustomDimension(1, useBuzzebeesAppStore.getState().clientVersion);
      useAnalyticsStore.getState().setCustomDimension(2, useBuzzebeesAppStore.getState().appId);

      const userResponse = await useUserStore.getState().fetchUser();
      if (userResponse.type === 'success') {
        useAnalyticsStore.getState().setCustomDimension(3, userResponse.model.Contact_Number ?? '');
        useAnalyticsStore.getState().setCustomDimension(4, userResponse.model.Email ?? '');
      }

      trackCommonEvents.loginSuccess();
    } else {
      set({ error: response });
    }

    return response;
  };

  return {
    isInitialized: false,
    versionData: null,
    rawVersionData: null,
    data: null,
    isLoading: false,
    isLoadingToken: false,
    error: null,
    token: null,
    isLoggedIn: false,

    setIsLoading: (isLoading: boolean) => set({ isLoading }),
    setIsLoadingToken: (isLoadingToken: boolean) => set({ isLoadingToken }),
    setError: (error: ErrorResponse | null) => set({ error }),

    checkLoggedIn: async () => {
      const { tokenFunctions } = useBuzzebeesAppStore.getState();
      if (!tokenFunctions) {
        set({ isLoggedIn: false, isInitialized: true });
        return;
      }

      try {
        set({ isLoadingToken: true });
        const token = await tokenFunctions.getToken();
        if (token) {
          set({ isLoggedIn: true, token });
        } else {
          set({ isLoggedIn: false, token: null });
        }
      } catch (error) {
        set({ isLoggedIn: false, token: null });
      } finally {
        set({ isLoadingToken: false, isInitialized: true });
      }
    },

    loginWithUsernamePassword: async (username: string, password: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.usernamePasswordLogin({
            username,
            password,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
          }),
      });
    },

    loginWithGoogle: async (token: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.googleLogin({
            idToken: token,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
          }),
      });
    },

    loginWithFacebook: async (token: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.facebookLogin({
            accessToken: token,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
          }),
      });
    },

    loginWithApple: async (token: string, refreshToken: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.appleLogin({
            idToken: token,
            refreshToken,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
          }),
      });
    },

    loginWithUUID: async () => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.deviceLogin({
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
          }),
      });
    },

    logout: async () => {
      const { tokenFunctions } = useBuzzebeesAppStore.getState();
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService || !tokenFunctions) {
        return { type: 'client-error', message: 'Service or auth persistence not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.logout({ uuid });
      set({ isLoading: false });

      tokenFunctions.removeToken();
      set({
        data: null,
        token: null,
        isLoggedIn: false,
        error: null,
      });

      return response;
    },

    forgetPassword: async (contact: string, type: 'email' | 'contact_number') => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.forgetPassword({ contact, type });
      set({ isLoading: false });
      return response;
    },

    resetPassword: async (contact: string, otp: string, refCode: string, password: string) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.resetPassword({
        contact,
        otp,
        refCode,
        newPassword: password,
      });
      set({ isLoading: false });
      return response;
    },

    sendOtp: async (contact: string, channel: string) => {
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.otp({
        uuid,
        appId,
        contactNumber: contact,
        channel,
      });
      set({ isLoading: false });
      return response;
    },

    validateOtp: async (otp: string, refCode: string, contact: string, channel: string) => {
      const appId = useBuzzebeesAppStore.getState().appId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.validateOtp({
        appId,
        otp,
        refCode,
        contactNumber: contact,
        use: false,
        channel,
      });
      set({ isLoading: false });
      return response;
    },

    confirmOtp: async (otp: string, refCode: string, contact: string) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.confirmOtp({
        otp,
        refCode,
        contactNumber: contact,
      });
      set({ isLoading: false });
      return response;
    },

    resume: async (clientVersion: string) => {
      const { uuid, appId, fcmToken } = useBuzzebeesAppStore.getState();
      const { tokenFunctions, tokenType } = useBuzzebeesAppStore.getState();
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService || !tokenFunctions) {
        return { type: 'client-error', message: 'Service or auth persistence not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.resume({
        uuid,
        appId,
        clientVersion,
        deviceToken: fcmToken,
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        const token = tokenType === 'jwt' ? response.model.jwt! : response.model.token!;
        tokenFunctions.setToken(token);
        set({
          data: response.model,
          error: null,
        });
      } else {
        set({ error: response });
      }

      return response;
    },

    version: async (clientVersion: string) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.versionRaw(clientVersion);
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ versionData: response.model, rawVersionData: response.model });
      }

      return response;
    },

    clear: () => {
      set({
        data: null,
        versionData: null,
        isLoading: false,
        error: null,
        token: null,
        isLoggedIn: false,
      });
    },
  };
});
